import 'brace';
import 'brace/ext/language_tools';
// Importing the searchbox extension will allow users to search via CMD+F
import 'brace/ext/searchbox';
import 'brace/mode/sql';
import {noop} from '@benchling/js-utils';
import React, {MutableRefObject, useEffect, useRef} from 'react';
import AceEditor from 'react-ace';

import useOnMount from 'ui-library/hooks/useOnMount';

import ParameterizedSQLMode from './ParameterizedSQLMode';

/**
 * Wrap the value arg in a ref. See SQLEditor for why we need this.
 */
function useValueAsRef<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export interface SQLEditorProps {
  className?: string;
  editorRef?: MutableRefObject<AceEditor | null>;
  height: string;
  onQueryChange: (newQuery: string) => void;
  onRun?: () => void;
  onTextSelected?: (selectedText: string | null) => void;
  query: string;
  showGutter: boolean;
  width: string;
}

export default function SQLEditor(props: SQLEditorProps): JSX.Element {
  // NOTE: When modifying this component, please make sure to test all usages of SQLEditor.
  // Currently this includes:
  //   - SQLEditorPane
  //   - WarehouseDiagramSQLEditor
  const defaultEditorRef = useRef<AceEditor | null>(null);
  const editorRef = props.editorRef ?? defaultEditorRef;

  useOnMount(() => {
    const customMode = new ParameterizedSQLMode();
    // @ts-expect-error The AceEditor type does not come with the "editor" property, but it is documented to exist
    // https://github.com/securingsincity/react-ace/blob/master/docs/FAQ.md
    const editor = editorRef.current!.editor;

    editor.getSession().setMode(customMode);

    editor.session.selection.on('changeSelection', () => {
      const selectedText: string = editor.getSelectedText();
      if (props.onTextSelected) {
        props.onTextSelected(selectedText || null);
      }
    });
  });

  const onRun = props.onRun ?? noop;
  // AceEditor only binds its commands on initial render. So as the props and state change,
  // AceEditor does not re-bind the callback with their new values. This means that if, for example, the user
  // edits the SQL query and hits Cmd-Enter to re-run, AceEditor can call an old saveAndRunQuery (running the
  // old query), instead of the newest one.
  //
  // To properly handle this, we define a normal function and wrap it as a ref, and call the ref in the
  // callback.
  const onRunRef = useValueAsRef(onRun);
  const commands = [
    {
      name: 'run',
      bindKey: {win: 'Ctrl-Enter', mac: 'Command-Enter'},
      exec: () => {
        onRunRef.current();
      },
    },
  ];

  return (
    <AceEditor
      // Query
      value={props.query}
      onChange={props.onQueryChange}
      // Callbacks
      commands={commands}
      ref={editorRef}
      // Code styling
      mode="sql"
      enableLiveAutocompletion={true}
      showGutter={props.showGutter}
      showPrintMargin={false}
      wrapEnabled={true}
      // Editor style
      className={props.className}
      editorProps={{
        $blockScrolling: Infinity,
      }}
      width={props.width}
      height={props.height}
    />
  );
}
